import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Dashboard.css';

const QRProfile = () => {
  const { qrCodeId } = useParams();
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPatientData = useCallback(async () => {
    try {
      const res = await api.get(`/api/users/qr/${qrCodeId}`);
      setPatientData(res.data.user);
      toast.success('Individual information loaded');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load individual data');
    } finally {
      setLoading(false);
    }
  }, [qrCodeId]);

  useEffect(() => {
    loadPatientData();
  }, [loadPatientData]);

  if (loading) {
    return <div className="loading">Loading individual information...</div>;
  }

  if (!patientData) {
    return (
      <div className="error-container">
        <h2>Individual Not Found</h2>
        <p>The QR code ID provided does not match any individual records.</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="patient-profile">
        <h1>Individual Medical Information</h1>
        
        <div className="patient-header">
          <h2>{patientData.firstName} {patientData.lastName}</h2>
          <span className="access-notice">🔒 This access has been logged</span>
        </div>

        {patientData.medicalInfo && (
          <div className="medical-info-display">
            <div className="info-section">
              <h3>Basic Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Blood Type:</label>
                  <span>{patientData.medicalInfo.bloodType || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Date of Birth:</label>
                  <span>
                    {patientData.medicalInfo.dateOfBirth 
                      ? new Date(patientData.medicalInfo.dateOfBirth).toLocaleDateString() 
                      : 'Not provided'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Height:</label>
                  <span>{patientData.medicalInfo.height || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Weight:</label>
                  <span>{patientData.medicalInfo.weight || 'Not provided'}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h3>Allergies</h3>
              {patientData.medicalInfo.allergies?.length > 0 ? (
                <ul className="medical-list">
                  {patientData.medicalInfo.allergies.map((allergy, idx) => (
                    <li key={idx} className="alert-item">{allergy}</li>
                  ))}
                </ul>
              ) : (
                <p className="no-data">No allergies reported</p>
              )}
            </div>

            <div className="info-section">
              <h3>Current Medications</h3>
              {patientData.medicalInfo.medications?.length > 0 ? (
                <ul className="medical-list">
                  {patientData.medicalInfo.medications.map((med, idx) => (
                    <li key={idx}>{med}</li>
                  ))}
                </ul>
              ) : (
                <p className="no-data">No current medications</p>
              )}
            </div>

            <div className="info-section">
              <h3>Medical Conditions</h3>
              {patientData.medicalInfo.conditions?.length > 0 ? (
                <ul className="medical-list">
                  {patientData.medicalInfo.conditions.map((condition, idx) => (
                    <li key={idx}>{condition}</li>
                  ))}
                </ul>
              ) : (
                <p className="no-data">No conditions reported</p>
              )}
            </div>

            {patientData.medicalInfo.emergencyContact?.name && (
              <div className="info-section emergency">
                <h3>Emergency Contact</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{patientData.medicalInfo.emergencyContact.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{patientData.medicalInfo.emergencyContact.phone}</span>
                  </div>
                  <div className="info-item">
                    <label>Relationship:</label>
                    <span>{patientData.medicalInfo.emergencyContact.relationship}</span>
                  </div>
                </div>
              </div>
            )}

            {patientData.medicalInfo.insuranceInfo?.provider && (
              <div className="info-section">
                <h3>Insurance Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Provider:</label>
                    <span>{patientData.medicalInfo.insuranceInfo.provider}</span>
                  </div>
                  <div className="info-item">
                    <label>Policy Number:</label>
                    <span>{patientData.medicalInfo.insuranceInfo.policyNumber}</span>
                  </div>
                </div>
              </div>
            )}

            {patientData.medicalInfo.notes && (
              <div className="info-section">
                <h3>Additional Notes</h3>
                <p className="notes-text">{patientData.medicalInfo.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRProfile;
